import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Star, Clock, ArrowRight, Zap, Award } from "lucide-react";
import { MaskedLine } from "@/lib/Reveal";
import { BUSINESS } from "@/lib/data";

const HERO_IMG = "/images/work-service.webp";

export const Hero = ({ onBook }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const glowX = useSpring(mx, { stiffness: 50, damping: 20 });
  const glowY = useSpring(my, { stiffness: 50, damping: 20 });
  const tiltX = useSpring(useMotionValue(0), { stiffness: 60, damping: 18 });
  const tiltY = useSpring(useMotionValue(0), { stiffness: 60, damping: 18 });

  const { scrollYProgress } = useScroll();
  const imgY = useTransform(scrollYProgress, [0, 0.25], [0, 80]);

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    mx.set(x);
    my.set(y);
    tiltY.set((x - 0.5) * 8);
    tiltX.set((0.5 - y) * 8);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={handleMouse}
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
    >
      <motion.div
        className="absolute w-[60vw] h-[60vw] rounded-full pointer-events-none"
        style={{
          left: useTransform(glowX, (v) => `calc(${v * 100}% - 30vw)`),
          top: useTransform(glowY, (v) => `calc(${v * 100}% - 30vw)`),
          background: "radial-gradient(circle, rgba(255,85,0,0.13) 0%, transparent 60%)",
        }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-4 py-2 mb-8"
            data-testid="hero-status-pill"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
            <span className="font-mono text-xs tracking-[0.18em] text-emerald-300 uppercase">
              24/7 Emergency Response · Engineers on call now
            </span>
          </motion.div>

          <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-8xl text-white">
            <MaskedLine delay={0.15}>No heat.</MaskedLine>
            <MaskedLine delay={0.3}>No hot water.</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-torch">No panic.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-neutral-400 leading-relaxed"
          >
            Your local plumber & heating engineer, keeping South Devon warm — from Exeter
            to Torbay. Fixed-price boiler, fire, cooker and hob servicing, 24/7 breakdowns,
            and all general plumbing — toilets, basins, sinks, taps, leaks and new radiators —
            booked and paid online in under two minutes, with a Gas Safe engineer at your door.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onBook}
              data-testid="hero-book-btn"
              className="group bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-8 py-4 rounded-sm flex items-center gap-3 transition-all hover:shadow-[0_0_36px_rgba(255,85,0,0.5)]"
            >
              Book a Boiler Service — £85.99
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-emergency-btn"
              className="group border border-neutral-700 hover:border-torch text-neutral-200 hover:text-torch font-display font-bold uppercase tracking-wide px-8 py-4 rounded-sm flex items-center gap-3 transition-colors"
            >
              <Zap className="w-4 h-4" />
              Emergency Callout
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-4"
            data-testid="hero-trust-badges"
          >
            {[
              { icon: ShieldCheck, text: `Gas Safe Registered #${BUSINESS.gasSafe}` },
              { icon: Star, text: "5.0 · 26 Google Reviews" },
              { icon: Clock, text: "Avg. emergency arrival 45 mins" },
              { icon: Award, text: "Best Newcomer 2026 Finalist" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2.5">
                <Icon className="w-4 h-4 text-torch" />
                <span className="font-mono text-xs tracking-wider text-neutral-400 uppercase">{text}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: tiltX, rotateY: tiltY, y: imgY, transformStyle: "preserve-3d", perspective: 800 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-torch/40 translate-x-5 translate-y-5 pointer-events-none" />
            <div className="relative overflow-hidden rounded-sm">
              <img
                src={HERO_IMG}
                alt="Teign Gas engineer servicing a boiler"
                className="w-full h-[560px] object-cover grayscale-[35%] contrast-125"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 border border-white/15 bg-black/60 backdrop-blur-md px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-torch uppercase">Live Job // TQ12</p>
                  <p className="font-display font-bold text-white text-sm mt-1">Combi breakdown — fixed in 52 mins</p>
                </div>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse-dot" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
